import { useEffect, useState } from "react";
import { Link as RouterLink, Navigate, RouteObject, useParams } from "react-router-dom";
import { useFliptContext } from "@flipt-io/flipt-client-react";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Skeleton, Button, CircularProgress, Stack, IconButton, Tooltip } from "@mui/material";
import CollaboratorsModal from "datasets/components/CollaboratorsModal";
import useDatasetAssistant from "datasets/components/assistant/hooks/useDatasetAssistant";
import useDataset from "datasets/hooks/useDataset";
import useIsMasterDataset from "datasets/hooks/useIsMasterDataset";
import DatasetAnalysis from "datasets/pages/analysis/DatasetAnalysis";
import DatasetReport from "datasets/pages/dashboards/pages/DatasetReport";
import DatasetReports from "datasets/pages/dashboards/pages/DatasetReports";
import DatasetFiles from "datasets/pages/files/DatasetFiles";
import DatasetOverview from "datasets/pages/overview/DatasetOverview";
import DatasetSettings from "datasets/pages/settings/DatasetSettings";
import DatasetTables from "datasets/pages/tables/DatasetTables";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import yn from "yn";
import { TitleBreadcrumbs } from "~/components/AppHeader";
import { ProtectedAppRoute } from "~/components/ProtectedAppRoute";
import RouteTabs, { RouteTab } from "~/components/RouteTabs";
import useAppHeader from "~/contexts/header";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useSafeRoutes } from "~/hooks/useSafeRoutes";
import useUserPermission from "~/hooks/useUserPermission";
import { ROUTE_404 } from "~/router/constants";
import { trackEvent } from "~/utils/monitoring";

const { VITE_DATASETS_ENABLED, VITE_DATASET_FILES_ENABLED, VITE_DATASET_PERMISSIONS_ENABLED } =
  import.meta.env;

// Create a wrapper component for the analysis route until we have a proper permission defined
const AnalysisRoute = () => {
  const assistantEnabled = useDatasetAssistant();
  const { data: dataset } = useDataset();
  return assistantEnabled && dataset?.aiAgentsEnabled === true ? <DatasetAnalysis /> : null;
};

const routeDefinitions: RouteObject[] = [
  { index: true, element: <Navigate to="overview" replace /> },
  { path: "overview", element: <DatasetOverview /> },
  {
    path: "reports",
    element: (
      <ProtectedAppRoute permission="list:catalog-report">
        <DatasetReports />
      </ProtectedAppRoute>
    ),
  },
  {
    path: "analysis",
    element: <AnalysisRoute />,
  },
  {
    path: "reports/:reportId",
    element: (
      <ProtectedAppRoute permission="list:catalog-report">
        <DatasetReport />
      </ProtectedAppRoute>
    ),
  },
  {
    path: "tables/*",
    element: (
      <ProtectedAppRoute permission="list:catalog-table">
        <DatasetTables />
      </ProtectedAppRoute>
    ),
  },
  {
    path: "files/:prefixId?/*",
    element: (
      <ProtectedAppRoute permission="list:catalog-file">
        <DatasetFiles />
      </ProtectedAppRoute>
    ),
  },

  {
    path: "settings/*",
    element: <DatasetSettings />,
  },
];

const DatasetDetails = () => {
  const user = useUser();
  const permissions = user?.permissions ?? [];
  const { datasetId, "*": subRoute } = useParams<{ datasetId: string; "*": string }>();
  const routes = useSafeRoutes(routeDefinitions);
  const { data: dataset, isLoading, error } = useDataset(datasetId);
  const isMasterDataset = useIsMasterDataset();
  const hasEditPermission = useUserPermission("edit:catalog-dataset");
  const showSettings = yn(VITE_DATASET_PERMISSIONS_ENABLED)
    ? dataset?.access_level === DatasetAccessLevel.OWNER
    : hasEditPermission;
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const showAssistant = useDatasetAssistant();
  const { isLoading: isFliptLoading } = useFliptContext();
  const isPreviewUser = yn(VITE_DATASET_PERMISSIONS_ENABLED)
    ? dataset?.access_level === DatasetAccessLevel.PREVIEW
    : false;

  const currentTab = subRoute?.split("/")[0] || "overview";
  const isReportEmbed = currentTab === "reports" && subRoute?.split("/")[1];

  useDocumentTitle(dataset?.title);
  const { setTitle } = useAppHeader();

  // Track dataset views only if dataset or tab has changed
  useEffect(() => {
    if (dataset?.id && currentTab) {
      trackEvent("dataset_viewed", {
        dataset_id: dataset.id,
        page: currentTab === "reports" ? "dashboards" : currentTab,
      });
    }
  }, [dataset?.id, currentTab]);

  useEffect(() => {
    setTitle(
      <TitleBreadcrumbs
        crumbs={[
          { title: "Data Browser", href: "/data-browser" },
          { title: "Datasets", href: "/data-browser/datasets" },
          { title: isLoading ? <Skeleton width={75} /> : dataset?.title, href: location.pathname },
        ]}
      />
    );
  }, [dataset, isLoading, setTitle]);

  // Sanitize tab value to prevent MUI TabContext errors
  const getValidTabValue = () => {
    // Return null for settings - no tab will be active
    if (currentTab === "settings") {
      return null;
    }
    if (
      currentTab === "analysis" &&
      !isFliptLoading &&
      (!showAssistant || dataset?.aiAgentsEnabled !== true)
    ) {
      return "overview";
    }
    if (isPreviewUser) {
      const allowedTabs = ["overview", "reports"];
      if (!allowedTabs.includes(currentTab)) {
        return "overview";
      }
    }
    return currentTab;
  };

  if (!yn(VITE_DATASETS_ENABLED) || error?.status === 404) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (!showSettings && currentTab === "settings" && !isLoading) {
    return <Navigate to="/404" replace />;
  }

  if (
    currentTab === "analysis" &&
    !isLoading &&
    !isFliptLoading &&
    (!showAssistant || dataset?.aiAgentsEnabled !== true)
  ) {
    return <Navigate to="overview" replace />;
  }

  // Redirect preview users trying to access restricted routes
  if (isPreviewUser && !isLoading) {
    const restrictedRoutes = ["analysis", "tables", "files", "settings"];
    if (restrictedRoutes.includes(currentTab)) {
      return <Navigate to="overview" replace />;
    }
  }

  // Build tabs based on permissions and feature flags
  const tabs: RouteTab[] = [
    { label: "Overview", value: "overview" },
    {
      label: "AI Analysis",
      value: "analysis",
      visible: showAssistant && !isPreviewUser && dataset?.aiAgentsEnabled === true,
    },
    { label: "Dashboards", value: "reports", visible: permissions.includes("list:catalog-report") },
    {
      label: "Tables",
      value: "tables",
      visible: permissions.includes("list:catalog-table") && !isPreviewUser,
    },
    {
      label: "Files",
      value: "files",
      visible:
        yn(VITE_DATASET_FILES_ENABLED) &&
        permissions.includes("list:catalog-file") &&
        !isMasterDataset &&
        !isPreviewUser,
    },
  ];

  // Action buttons for the tab bar
  const renderActions = () => {
    const isDatasetPermissionsEnabled = yn(VITE_DATASET_PERMISSIONS_ENABLED);
    const accessLevel = dataset?.access_level;
    const showShare = isDatasetPermissionsEnabled && accessLevel === DatasetAccessLevel.OWNER;
    if (!showSettings && !showShare) {
      return null;
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {showSettings && (
          <Tooltip arrow enterDelay={300} placement="left" title="Settings">
            <IconButton
              data-testid="dataset-settings-button"
              component={currentTab === "settings" ? "button" : RouterLink}
              to={
                currentTab === "settings"
                  ? undefined
                  : `/data-browser/datasets/${datasetId}/settings`
              }
              size="small"
              sx={{
                backgroundColor: currentTab === "settings" ? "grey.900" : "grey.200",
                color: currentTab === "settings" ? "white" : "grey.800",
                height: 32,
                width: 32,
                "&:hover": {
                  backgroundColor: currentTab === "settings" ? "grey.900" : "grey.300",
                  color: currentTab === "settings" ? "white" : "grey.900",
                },
              }}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {showShare && (
          <Button
            data-testid="share-dataset-button"
            variant="contained"
            color="primary"
            onClick={() => setIsShareModalOpen(true)}
            sx={{ lineHeight: "16px", px: 1.5, py: 1 }}
          >
            Share
          </Button>
        )}
      </Stack>
    );
  };

  // Loading state
  const loadingContent = (
    <Stack
      height="100%"
      flex={1}
      alignItems="center"
      justifyContent="center"
      bgcolor="common.pageBackground"
    >
      <CircularProgress />
    </Stack>
  );

  return (
    <>
      <RouteTabs
        tabs={tabs}
        currentTab={getValidTabValue()}
        actions={renderActions()}
        showTabs={!isReportEmbed}
      >
        {isLoading ? loadingContent : routes}
      </RouteTabs>

      {isShareModalOpen && dataset && (
        <CollaboratorsModal
          open={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          dataset={dataset}
          datasetId={datasetId}
        />
      )}
    </>
  );
};

export default DatasetDetails;
